import { useState, useRef, useEffect, useCallback } from 'react';
import {
  TextInput,
  Button,
  Stack,
  Tag,
  InlineNotification,
  IconButton,
  OverflowMenu,
  OverflowMenuItem,
  Modal,
} from '@carbon/react';
import {
  Send,
  Bot,
  User,
  Renew,
  TextScale,
  CheckmarkFilled,
  WarningAltFilled,
  Add,
  Chat,
} from '@carbon/icons-react';
import { Node, Edge } from '@xyflow/react';
import ReactMarkdown from 'react-markdown';
import {
  chatApi,
  GraphRagContext,
  IndexStatus,
  ChatConversationSummary,
  StoredMessage,
} from '../../lib/api/chat.api';
import type { GraphProposal, AddNodeProposal, AddRelationProposal, EditNodeProposal } from '@snxt/snxt-onto-share';
import { ProposalCard, NearMatch } from './ProposalCard';

/**
 * Lookup index used to turn plain-text node mentions inside an assistant
 * message into clickable chips. Built from the GraphRAG context so we only
 * link concepts the model could actually have grounded on.
 */
interface NodeLookup {
  /** key = lowercased label OR lowercased nodeId, value = canonical nodeId */
  byKey: Record<string, { nodeId: string; label: string }>;
  /** Sorted list of distinct lowercase keys, longest first, for greedy regex. */
  keys: string[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  /** Citations from GraphRAG retrieval — surfaced under the answer. */
  citations?: GraphRagContext['seeds'];
  /** Total nodes in the ontology when this message was answered. */
  totalNodes?: number;
  /** Nodes still without an embedding when this message was generated. */
  embeddingsMissing?: number;
  /** Linkifying index for inline node mentions. */
  nodeLookup?: NodeLookup;
  /** Co-Ontologist graph mutation proposals attached to this message. */
  proposals?: GraphProposal[];
  /**
   * Decisions already made on proposals in this message, keyed by proposal index.
   * Lifted out of individual ProposalCard local state so sibling cards can see
   * each other's outcomes (e.g. block an add_relation until its add_node is accepted).
   */
  proposalDecisions?: Record<number, 'accepted' | 'rejected'>;
  /** Clickable example prompts shown below the welcome message. */
  examples?: string[];
}

/** Build a NodeLookup from the GraphRAG context's seeds + neighbours. */
function buildNodeLookup(ctx: GraphRagContext): NodeLookup {
  const byKey: Record<string, { nodeId: string; label: string }> = {};
  const all = [...(ctx.seeds || []), ...(ctx.neighbors || [])];
  for (const n of all) {
    if (!n?.nodeId || !n?.label) continue;
    const entry = { nodeId: n.nodeId, label: n.label };
    byKey[n.label.toLowerCase()] = entry;
    byKey[n.nodeId.toLowerCase()] = entry;
  }
  const keys = Object.keys(byKey).sort((a, b) => b.length - a.length);
  return { byKey, keys };
}

/**
 * Build a short textual summary of the proposals accepted in the most recent
 * messages, to be forwarded to the backend as `acceptedContext`.
 *
 * Exported (as opposed to inline) so it can be unit-tested without mounting
 * the full React component.
 */
export function buildAcceptedContext(messages: Pick<ChatMessage, 'proposals' | 'proposalDecisions'>[]): string | undefined {
  const lines: string[] = [];
  for (const m of messages) {
    if (!m.proposals || !m.proposalDecisions) continue;
    m.proposals.forEach((p, idx) => {
      if (m.proposalDecisions![idx] !== 'accepted') return;
      if (p.kind === 'add_node') {
        lines.push(`Added node: ${p.label}`);
      } else if (p.kind === 'add_relation') {
        lines.push(`Added relation: ${p.sourceNodeLabel} --[${p.label}]--> ${p.targetNodeLabel}`);
      } else if (p.kind === 'edit_node') {
        lines.push(`Edited node: ${p.label ?? p.currentLabel}`);
      }
    });
  }
  return lines.length > 0 ? lines.join('\n') : undefined;
}

/** Escape a string for safe inclusion inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Walk a string and split out matches of any node label/id, rendering each
 * match as a clickable chip. Non-match runs are returned as plain text. Used
 * by both the prose renderer and inline `<code>` matches.
 */
function linkifyText(
  text: string,
  lookup: NodeLookup | undefined,
  onClick: ((nodeId: string) => void) | undefined,
  chipFontSize: string,
): React.ReactNode {
  if (!lookup || lookup.keys.length === 0 || !text) return text;
  const pattern = new RegExp(
    '\\b(' + lookup.keys.map(escapeRegExp).join('|') + ')\\b',
    'gi',
  );
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.slice(lastIdx, match.index));
    }
    const hit = lookup.byKey[match[0].toLowerCase()];
    parts.push(
      <NodeChip
        key={`nc-${key++}-${match.index}`}
        label={match[0]}
        nodeId={hit.nodeId}
        onClick={onClick}
        fontSize={chipFontSize}
      />,
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

/**
 * Inline clickable chip for a node reference. Styled close to a `<code>`
 * span so prose stays readable, but with a hover affordance and a click
 * handler that focuses the node on the canvas.
 */
function NodeChip({
  label,
  nodeId,
  onClick,
  fontSize = '0.8125rem',
}: {
  label: string;
  nodeId: string;
  onClick?: (nodeId: string) => void;
  fontSize?: string;
}) {
  const clickable = !!onClick;
  return (
    <span
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick!(nodeId) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!(nodeId);
              }
            }
          : undefined
      }
      title={`Focus "${label}" on the canvas`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 0.375rem',
        margin: '0 1px',
        borderRadius: 12,
        fontSize,
        fontFamily: 'var(--cds-code-01-font-family, monospace)',
        background: 'var(--cds-tag-background-blue, #d0e2ff)',
        color: 'var(--cds-tag-color-blue, #0043ce)',
        border: '1px solid transparent',
        cursor: clickable ? 'pointer' : 'default',
        textDecoration: 'none',
        lineHeight: 1.4,
      }}
      onMouseEnter={(e) => {
        if (clickable)
          (e.currentTarget as HTMLSpanElement).style.borderColor =
            'var(--cds-tag-hover-blue, #0043ce)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLSpanElement).style.borderColor = 'transparent';
      }}
    >
      {label}
    </span>
  );
}

/**
 * Tiny markdown renderer tuned for the chat bubble:
 *   - inline elements only-ish (paragraphs/lists supported)
 *   - inline code uses Carbon's monospace token
 *   - external link safety
 */
type Density = 'normal' | 'compact';

/** Per-density font-size tokens used across the chat UI. */
const DENSITY_TOKENS: Record<Density, {
  body: string;
  code: string;
  meta: string;
  heading1: string;
  heading2: string;
  heading3: string;
  codeBlock: string;
  chip: string;
}> = {
  normal: {
    body: '0.875rem',     // 14px
    code: '0.8125rem',    // 13px
    meta: '0.6875rem',    // 11px
    heading1: '1rem',
    heading2: '0.9375rem',
    heading3: '0.875rem',
    codeBlock: '0.75rem',
    chip: '0.8125rem',
  },
  compact: {
    body: '0.6875rem',    // 11px
    code: '0.625rem',     // 10px
    meta: '0.5625rem',    // 9px
    heading1: '0.8125rem',
    heading2: '0.75rem',
    heading3: '0.6875rem',
    codeBlock: '0.625rem',
    chip: '0.625rem',
  },
};

const DENSITY_STORAGE_KEY = 'snxt.chat.density';

function MessageBody({
  children,
  lookup,
  onNodeClick,
  density,
}: {
  children: string;
  lookup?: NodeLookup;
  onNodeClick?: (nodeId: string) => void;
  density: Density;
}) {
  const tokens = DENSITY_TOKENS[density];
  /**
   * Recursively walk markdown text children and replace plain-text node
   * mentions with clickable chips. Anything that's not a string (already
   * an element such as <strong>, <em>, links, code) is left as-is.
   */
  const linkifyChildren = (kids: any): React.ReactNode => {
    if (kids == null) return kids;
    if (typeof kids === 'string') return linkifyText(kids, lookup, onNodeClick, tokens.chip);
    if (Array.isArray(kids)) return kids.map((c, i) => <span key={i}>{linkifyChildren(c)}</span>);
    return kids;
  };

  return (
    <ReactMarkdown
      components={{
        p: ({ node, children, ...props }: any) => (
          <p
            style={{
              margin: '0 0 0.5rem 0',
              fontSize: tokens.body,
              lineHeight: 1.45,
            }}
            {...props}
          >
            {linkifyChildren(children)}
          </p>
        ),
        ul: ({ node, ...props }) => (
          <ul
            style={{
              margin: '0 0 0.5rem 0',
              paddingLeft: '1.25rem',
              fontSize: tokens.body,
              lineHeight: 1.45,
            }}
            {...props}
          />
        ),
        ol: ({ node, ...props }) => (
          <ol
            style={{
              margin: '0 0 0.5rem 0',
              paddingLeft: '1.25rem',
              fontSize: tokens.body,
              lineHeight: 1.45,
            }}
            {...props}
          />
        ),
        li: ({ node, children, ...props }: any) => (
          <li
            style={{
              marginBottom: '0.125rem',
              fontSize: tokens.body,
              lineHeight: 1.45,
            }}
            {...props}
          >
            {linkifyChildren(children)}
          </li>
        ),
        strong: ({ node, children, ...props }: any) => (
          <strong style={{ fontSize: 'inherit' }} {...props}>
            {linkifyChildren(children)}
          </strong>
        ),
        em: ({ node, children, ...props }: any) => (
          <em style={{ fontSize: 'inherit' }} {...props}>
            {linkifyChildren(children)}
          </em>
        ),
        hr: ({ node, ...props }) => (
          <hr
            style={{
              border: 0,
              borderTop: '1px solid var(--cds-border-subtle-01)',
              margin: '0.5rem 0',
            }}
            {...props}
          />
        ),
        code: ({ node, className, children, ...props }: any) => {
          const isBlock = /language-/.test(className || '');
          if (isBlock) {
            return (
              <pre
                style={{
                  background: 'var(--cds-layer-accent-01)',
                  padding: '0.5rem',
                  borderRadius: 4,
                  overflowX: 'auto',
                  fontSize: tokens.codeBlock,
                  margin: '0.25rem 0 0.5rem 0',
                }}
              >
                <code {...props}>{children}</code>
              </pre>
            );
          }
          // Inline `code`: if it matches a known node, render the clickable
          // chip (this is the canonical citation form the system prompt asks
          // the LLM to use, so we want it to *always* be clickable).
          const text = typeof children === 'string'
            ? children
            : Array.isArray(children) && children.length === 1 && typeof children[0] === 'string'
              ? children[0]
              : null;
          if (text && lookup) {
            const hit = lookup.byKey[text.toLowerCase()];
            if (hit) {
              return (
                <NodeChip
                  label={text}
                  nodeId={hit.nodeId}
                  onClick={onNodeClick}
                  fontSize={tokens.chip}
                />
              );
            }
          }
          return (
            <code
              style={{
                fontFamily: 'var(--cds-code-01-font-family, monospace)',
                background: 'var(--cds-layer-accent-01)',
                padding: '0 0.25rem',
                borderRadius: 3,
                fontSize: tokens.code,
              }}
              {...props}
            >
              {children}
            </code>
          );
        },
        a: ({ node, ...props }) => (
          <a target="_blank" rel="noopener noreferrer" {...props} />
        ),
        h1: ({ node, ...props }) => (
          <h4 style={{ fontSize: tokens.heading1, margin: '0.25rem 0' }} {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h5 style={{ fontSize: tokens.heading2, margin: '0.25rem 0' }} {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h6 style={{ fontSize: tokens.heading3, margin: '0.25rem 0' }} {...props} />
        ),
        table: ({ node, ...props }) => (
          <div style={{ overflowX: 'auto', margin: '0.5rem 0' }}>
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: tokens.body,
                lineHeight: 1.4,
              }}
              {...props}
            />
          </div>
        ),
        thead: ({ node, ...props }) => (
          <thead
            style={{
              background: 'var(--cds-layer-accent-01)',
            }}
            {...props}
          />
        ),
        tbody: ({ node, ...props }) => <tbody {...props} />,
        tr: ({ node, ...props }) => (
          <tr
            style={{
              borderBottom: '1px solid var(--cds-border-subtle-01)',
            }}
            {...props}
          />
        ),
        th: ({ node, ...props }) => (
          <th
            style={{
              padding: '0.3125rem 0.625rem',
              textAlign: 'left',
              fontWeight: 600,
              fontSize: tokens.body,
              whiteSpace: 'nowrap',
            }}
            {...props}
          />
        ),
        td: ({ node, ...props }) => (
          <td
            style={{
              padding: '0.3125rem 0.625rem',
              fontSize: tokens.body,
              verticalAlign: 'top',
            }}
            {...props}
          />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

/**
 * Tiny header badge that shows whether the saved ontology is fully indexed
 * for vector search. Three visual states:
 *   - loading / no saved ontology yet → muted spinner-dot (neutral)
 *   - all nodes embedded              → green ✓ checkmark
 *   - some nodes missing embeddings   → amber ⚠ with count in tooltip
 */
function IndexStatusIcon({
  status,
  loading,
  hasOntologyId,
}: {
  status: IndexStatus | null;
  loading: boolean;
  hasOntologyId: boolean;
}) {
  // No saved ontology yet — render nothing (the "save first" message in the
  // chat itself is enough; the header should stay minimal).
  if (!hasOntologyId) {
    return (
      <span
        title="Save the ontology to enable vector search"
        aria-label="Indexing status: not saved"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--cds-icon-disabled, #c6c6c6)',
          marginLeft: '0.125rem',
          flexShrink: 0,
        }}
      />
    );
  }

  // First load — keep some visual placeholder so the title row doesn't shift.
  if (!status && loading) {
    return (
      <span
        title="Checking indexing status…"
        aria-label="Indexing status: loading"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--cds-icon-disabled, #c6c6c6)',
          marginLeft: '0.125rem',
          flexShrink: 0,
        }}
      />
    );
  }

  if (!status) return null;

  if (status.totalNodes === 0) {
    return (
      <span
        title="No concepts saved yet"
        aria-label="Indexing status: empty ontology"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--cds-icon-disabled, #c6c6c6)',
          marginLeft: '0.125rem',
          flexShrink: 0,
        }}
      />
    );
  }

  if (status.embeddingsMissing === 0) {
    return (
      <CheckmarkFilled
        size={16}
        aria-label={`All ${status.totalNodes} concepts indexed`}
      >
        <title>{`All ${status.totalNodes} concept${
          status.totalNodes === 1 ? '' : 's'
        } indexed for vector search`}</title>
      </CheckmarkFilled>
    );
  }

  // Partial — amber warning with the missing count.
  const indexed = status.totalNodes - status.embeddingsMissing;
  return (
    <WarningAltFilled
      size={16}
      style={{ fill: 'var(--cds-support-warning, #f1c21b)' }}
      aria-label={`${status.embeddingsMissing} of ${status.totalNodes} concepts not yet indexed`}
    >
      <title>{`${indexed}/${status.totalNodes} concepts indexed — ${status.embeddingsMissing} pending. Vector search may miss the latest changes; click "Re-index now" below to embed them.`}</title>
    </WarningAltFilled>
  );
}

interface ChatInterfaceProps {
  ontology: {
    id?: string;
    nodes: Node[];
    edges: Edge[];
    domain?: string;
  };
  contextData?: {
    nodes: Node[];
    edges: Edge[];
  };
  /** Called when the user clicks a citation chip → focus that node on the canvas. */
  onCitationClick?: (nodeId: string) => void;
  /** Called when the user accepts an add_node proposal. */
  onAddNode?: (proposal: AddNodeProposal) => void;
  /** Called when the user accepts an add_relation proposal. */
  onAddEdge?: (proposal: AddRelationProposal) => void;
  /** Called when the user accepts an edit_node proposal. */
  onUpdateNode?: (proposal: EditNodeProposal) => void;
  /**
   * Called after every accepted proposal, after the graph mutation callbacks.
   * Intended for auto-save: the caller should trigger a save so newly-accepted
   * nodes are indexed and immediately visible to the assistant's vector search.
   * Only called when ontology.id is present (unsaved ontologies are skipped).
   */
  onProposalAccepted?: () => void;
}

/** Initial welcome message — shared by all "new conversation" reset points. */
const WELCOME_MESSAGE: ChatMessage = {
  id: '1',
  role: 'assistant',
  content:
    "Hi! I'm your Co-Ontologist. I can answer questions about your ontology using semantic search and 1-hop graph expansion, with citations you can click to focus on the canvas. I can also propose graph changes — adding nodes or relations, editing existing nodes — which you can review and accept right here in the chat.",
  timestamp: new Date(),
  examples: [
    'What are the main concepts in this ontology?',
    'Add a node called "Protein Folding"',
    'Link "Enzyme" to "Substrate" with a relates-to relation',
    'Update the description of the most central concept',
  ],
};

export const ChatInterface = ({ ontology, contextData, onCitationClick, onAddNode, onAddEdge, onUpdateNode, onProposalAccepted }: ChatInterfaceProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([{ ...WELCOME_MESSAGE, timestamp: new Date() }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracked per-ontology so the "Re-index" CTA only shows after we've actually
  // observed a degraded retrieval, and disappears once it's fixed.
  const [pendingMissing, setPendingMissing] = useState(0);
  const [reindexing, setReindexing] = useState(false);
  // Background indexing normally finishes within 5-10s. We only surface the
  // "Re-index now" CTA if embeddings are still missing after this grace
  // period — otherwise the button flashes on every save and confuses users.
  const [showReindexCta, setShowReindexCta] = useState(false);
  const REINDEX_CTA_DELAY_MS = 20_000;

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Chat History — multi-conversation state. `conversations` is the list for
  // the current ontology (summaries only); `activeConversationId` is null
  // until either one is restored on mount or the first message is sent
  // (which lazily creates one).
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // Prevents overlapping persistMessages calls (e.g. rapid sends) from
  // racing each other and creating two conversations for the same session.
  const saveInFlightRef = useRef(false);

  // Near-match results from duplicate-check similarity searches.
  // Key = "messageId:proposalIdx". Populated when the user clicks Accept on an
  // add_node card; cleared when they confirm ("Add anyway") or cancel.
  const [nearMatchesMap, setNearMatchesMap] = useState<Record<string, NearMatch[]>>({});

  // Indexing status surfaced in the header. `null` = not loaded / no saved
  // ontology yet, otherwise the latest counts from the backend. We refresh:
  //   - on mount / when the ontology id changes
  //   - whenever the ontology's local node count changes (proxy for "saved")
  //   - on a slow poll while we know there are pending embeddings, so the
  //     icon flips green automatically once the backend backfill catches up
  const [indexStatus, setIndexStatus] = useState<IndexStatus | null>(null);
  const [indexStatusLoading, setIndexStatusLoading] = useState(false);
  const localNodeCount = ontology.nodes?.length ?? 0;

  const fetchIndexStatus = async (ontologyId: string) => {
    setIndexStatusLoading(true);
    try {
      const status = await chatApi.indexStatus(ontologyId);
      setIndexStatus(status);
      // Note: we deliberately do NOT mirror `embeddingsMissing` into
      // `pendingMissing` here. Right after a save, the backend almost always
      // reports a few missing embeddings for 5-10 seconds while the
      // background backfill catches up. Showing the "Re-index now" CTA
      // immediately would flash on every save. Instead, the header icon
      // (driven by `indexStatus`) reflects the live state, and we only
      // surface the CTA after a sustained stall (see effect below) or when
      // a real query comes back with degraded retrieval.
      if (status.embeddingsMissing === 0) {
        // Indexing fully caught up → hide any stale CTA.
        setPendingMissing(0);
        setShowReindexCta(false);
      }
    } catch {
      // Silent — header just stays in unknown state. We don't want a noisy
      // toast every few seconds while the user is typing.
    } finally {
      setIndexStatusLoading(false);
    }
  };

  // Restore the most recent (non-archived) conversation for this ontology
  // when it opens, and load the switcher's conversation list. Silent on
  // failure — an empty/unsaved ontology or a fresh backend simply starts
  // with no conversations, and the first message send lazily creates one.
  useEffect(() => {
    if (!ontology.id) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await chatApi.listConversations(ontology.id!);
        if (cancelled) return;
        setConversations(list);
        const most = list[0]; // already sorted by last_message_at DESC
        if (most) {
          const full = await chatApi.getConversation(most.id);
          if (cancelled) return;
          if (full.messages.length > 0) {
            setMessages(
              full.messages.map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: new Date(m.timestamp),
                citations: m.citations as any,
                totalNodes: m.totalNodes,
                embeddingsMissing: m.embeddingsMissing,
                nodeLookup: m.nodeLookup as any,
                proposals: m.proposals,
                proposalDecisions: m.proposalDecisions,
              })),
            );
          }
          setActiveConversationId(most.id);
        }
      } catch {
        // Silent — first session will auto-create on first send.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ontology.id]);

  useEffect(() => {
    if (!ontology.id) {
      setIndexStatus(null);
      return;
    }
    fetchIndexStatus(ontology.id);
  }, [ontology.id, localNodeCount]);

  // Slow background poll while indexing is incomplete, so the header icon
  // flips to "all indexed" without requiring user interaction.
  useEffect(() => {
    if (!ontology.id) return;
    if (!indexStatus || indexStatus.embeddingsMissing === 0) return;
    const handle = window.setInterval(() => {
      if (ontology.id) fetchIndexStatus(ontology.id);
    }, 5000);
    return () => window.clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ontology.id, indexStatus?.embeddingsMissing]);

  // "Re-index now" stall timer. The button is intentionally a last-resort
  // option: in the happy path the backend backfills new embeddings within
  // 5-10 seconds. We only surface the CTA if `embeddingsMissing > 0` is
  // *still* true after `REINDEX_CTA_DELAY_MS` of polling — that strongly
  // suggests the worker is wedged or the user actually needs to retry.
  useEffect(() => {
    if (!ontology.id) {
      setShowReindexCta(false);
      return;
    }
    if (!indexStatus || indexStatus.embeddingsMissing === 0) {
      // Caught up → no CTA needed.
      setShowReindexCta(false);
      return;
    }
    // Arm a one-shot timer; if embeddings are still missing when it fires,
    // unhide the CTA. The effect re-runs whenever the missing count changes,
    // which naturally cancels the timer if indexing completes in time.
    const handle = window.setTimeout(() => {
      setShowReindexCta(true);
    }, REINDEX_CTA_DELAY_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the count, not the object identity
  }, [ontology.id, indexStatus?.embeddingsMissing]);

  // Font-size density toggle (compact ↔ normal). Persisted in localStorage so
  // the user's preference survives reloads and works across editor sessions.
  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === 'undefined') return 'normal';
    const saved = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return saved === 'compact' ? 'compact' : 'normal';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    }
  }, [density]);
  const tokens = DENSITY_TOKENS[density];
  const toggleDensity = () =>
    setDensity((d) => (d === 'compact' ? 'normal' : 'compact'));

  // Auto-scroll only when a new message is appended (length increases) or
  // while loading — not when a proposal decision is recorded on an existing
  // message (which would otherwise scroll to the bottom every time the user
  // clicks Accept).
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isLoading]);

  /**
   * Compose a user-supplied message + selection context (Ctrl-click) hint
   * into a single prompt the retrieval step can act on. Selection context is
   * surfaced as a hint, not as the only signal — retrieval can still find
   * other relevant nodes via embeddings.
   */
  function buildAugmentedQuery(rawInput: string): string {
    if (!contextData?.nodes?.length) return rawInput;
    const selectedLabels = contextData.nodes
      .map((n) => (n.data as any)?.label)
      .filter(Boolean)
      .slice(0, 5);
    if (selectedLabels.length === 0) return rawInput;
    return `${rawInput}\n\n[User has highlighted these concepts on the canvas: ${selectedLabels.join(', ')}]`;
  }

  /** Convert local ChatMessage state to the wire shape saved on the backend. */
  const toStoredMessages = useCallback((msgs: ChatMessage[]): StoredMessage[] =>
    msgs.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp.toISOString(),
      citations: m.citations as any,
      totalNodes: m.totalNodes,
      embeddingsMissing: m.embeddingsMissing,
      nodeLookup: m.nodeLookup as any,
      proposals: m.proposals,
      proposalDecisions: m.proposalDecisions,
    })), []);

  /**
   * Persist the full message thread. Lazily creates a conversation on the
   * first call (auto-titled from the first user message) and returns its id
   * so the caller can adopt it as the active conversation. Failures are
   * swallowed — chat persistence must never break the chat UX itself.
   */
  const persistMessages = useCallback(
    async (msgs: ChatMessage[], currentConvId: string | null): Promise<string | null> => {
      if (!ontology.id || saveInFlightRef.current) return currentConvId;
      saveInFlightRef.current = true;
      try {
        const stored = toStoredMessages(msgs);
        let convId = currentConvId;
        if (!convId) {
          const firstUser = msgs.find((m) => m.role === 'user');
          const autoTitle = firstUser
            ? firstUser.content.slice(0, 60) + (firstUser.content.length > 60 ? '…' : '')
            : 'New conversation';
          const created = await chatApi.createConversation(ontology.id, autoTitle);
          convId = created.id;
          setConversations((prev) => [
            { ...created, messages: undefined as any },
            ...prev,
          ]);
        }
        const firstUser = msgs.find((m) => m.role === 'user');
        const autoTitle = firstUser
          ? firstUser.content.slice(0, 60) + (firstUser.content.length > 60 ? '…' : '')
          : 'New conversation';
        const updated = await chatApi.updateConversation(convId, { messages: stored, title: autoTitle });
        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId ? { ...c, title: updated.title, lastMessageAt: updated.lastMessageAt } : c,
          ),
        );
        return convId;
      } catch {
        return currentConvId;
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [ontology.id, toStoredMessages],
  );

  // Re-persist whenever proposal decisions change (accept/reject clicks happen
  // outside sendMessage so they wouldn't otherwise be saved).
  const decisionsSnapshot = messages
    .map((m) => m.proposalDecisions ? JSON.stringify(m.proposalDecisions) : '')
    .join('|');
  useEffect(() => {
    if (!activeConversationId) return;
    void persistMessages(messages, activeConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionsSnapshot]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const trimmed = input.trim();
    const userMessage: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      if (!ontology.id) {
        throw new Error('Save your ontology first — I need a saved version to query the embeddings.');
      }

      // 1. Retrieve relevant subgraph from the backend (vector + 1-hop).
      const augmented = buildAugmentedQuery(trimmed);
      const ctx = await chatApi.retrieveContext({
        ontologyId: ontology.id,
        query: augmented,
      });

      // 2. Ask the Co-Ontologist via the propose endpoint (tool-use enabled).
      //    Falls back to the plain completion endpoint when propose is unavailable
      //    (e.g. the user has no AI config) so that the chat is never silently broken.
      const priorMessages = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-6) // last 3 turns for context
        .map((m) => ({ role: m.role, content: m.content }));

      const acceptedContext = buildAcceptedContext(messages.slice(-6));

      const propose = await chatApi.propose({
        ontologyId: ontology.id,
        message: trimmed,
        messages: priorMessages,
        graphContext: ctx.promptContext,
        acceptedContext,
      });

      const answer = propose.text?.trim() || '(no text reply)';

      const assistantMessage: ChatMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: answer,
        timestamp: new Date(),
        citations: ctx.seeds,
        totalNodes: ctx.totalNodes,
        embeddingsMissing: ctx.embeddingsMissing,
        nodeLookup: buildNodeLookup(ctx),
        proposals: propose.proposals.length > 0 ? propose.proposals : undefined,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setPendingMissing(ctx.embeddingsMissing ?? 0);

      // Persistence must NOT be called inside the setMessages updater —
      // updater functions must be pure (no side-effects), and the captured
      // activeConversationId would be stale if called from inside the
      // setter. Build the post-send thread explicitly instead.
      const nextMessages = [...messages, userMessage, assistantMessage];
      void persistMessages(nextMessages, activeConversationId).then((newId) => {
        if (newId && newId !== activeConversationId) setActiveConversationId(newId);
      });
    } catch (err: any) {
      const msg = err?.message || 'Something went wrong while answering';
      setError(msg);
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ ${msg}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
      backgroundColor: 'var(--cds-layer-01)'
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem',
        borderBottom: '1px solid var(--cds-border-subtle-01)',
        backgroundColor: 'var(--cds-layer-02)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--cds-text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: 0,
          }}>
            <Bot size={20} />
            co-Ontologist
            <IndexStatusIcon
              status={indexStatus}
              loading={indexStatusLoading}
              hasOntologyId={!!ontology.id}
            />
          </h3>
          <IconButton
            kind="ghost"
            size="sm"
            label={
              density === 'compact'
                ? 'Switch to normal text size'
                : 'Switch to compact text size'
            }
            align="bottom-right"
            onClick={toggleDensity}
          >
            <TextScale size={16} />
          </IconButton>
        </div>
        <p style={{
          fontSize: '0.75rem',
          color: 'var(--cds-text-secondary)',
          marginTop: '0.25rem'
        }}>
          {contextData ? `${contextData.nodes.length} concepts selected` : 'No context selected'}
        </p>
        {ontology.id && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            marginTop: '0.5rem',
          }}>
            <select
              aria-label="Conversation"
              value={activeConversationId ?? ''}
              onChange={async (e) => {
                const id = e.target.value;
                if (!id) return;
                try {
                  const full = await chatApi.getConversation(id);
                  const restored = full.messages.map((m) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                    timestamp: new Date(m.timestamp),
                    citations: m.citations as any,
                    totalNodes: m.totalNodes,
                    embeddingsMissing: m.embeddingsMissing,
                    nodeLookup: m.nodeLookup as any,
                    proposals: m.proposals,
                    proposalDecisions: m.proposalDecisions,
                  }));
                  setMessages(
                    restored.length > 0
                      ? restored
                      : [{ ...WELCOME_MESSAGE, timestamp: new Date() }],
                  );
                  setActiveConversationId(id);
                } catch {
                  // Silent — keep the current thread if the fetch fails.
                }
              }}
              style={{
                flex: 1,
                fontSize: tokens.meta,
                padding: '0.25rem 0.375rem',
                borderRadius: 4,
                border: '1px solid var(--cds-border-subtle-01)',
                background: 'var(--cds-field-01)',
                color: 'var(--cds-text-primary)',
              }}
            >
              {activeConversationId === null && (
                <option value="" disabled>
                  New conversation…
                </option>
              )}
              {conversations
                .filter((c) => showArchived || !c.isArchived)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.isArchived ? '📦 ' : ''}
                    {c.title}
                  </option>
                ))}
            </select>
            <IconButton
              kind="ghost"
              size="sm"
              label="Start new conversation"
              onClick={() => {
                setActiveConversationId(null);
                setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date() }]);
              }}
            >
              <Add size={16} />
            </IconButton>
            {activeConversationId && (
              <OverflowMenu size="sm" flipped renderIcon={Chat} aria-label="Conversation actions">
                <OverflowMenuItem
                  itemText="Rename"
                  onClick={() => {
                    const current = conversations.find((c) => c.id === activeConversationId);
                    setRenamingId(activeConversationId);
                    setRenameValue(current?.title ?? '');
                    setRenameModalOpen(true);
                  }}
                />
                <OverflowMenuItem
                  itemText={
                    conversations.find((c) => c.id === activeConversationId)?.isArchived
                      ? 'Unarchive'
                      : 'Archive'
                  }
                  onClick={async () => {
                    const current = conversations.find((c) => c.id === activeConversationId);
                    if (!current) return;
                    const isArchived = !current.isArchived;
                    try {
                      const updated = await chatApi.updateConversation(current.id, { isArchived });
                      setConversations((prev) =>
                        prev.map((c) => (c.id === current.id ? { ...c, isArchived: updated.isArchived } : c)),
                      );
                    } catch {
                      // Silent
                    }
                  }}
                />
                <OverflowMenuItem
                  hasDivider
                  isDelete
                  itemText="Delete"
                  onClick={async () => {
                    if (!activeConversationId) return;
                    try {
                      await chatApi.deleteConversation(activeConversationId);
                      setConversations((prev) => prev.filter((c) => c.id !== activeConversationId));
                      setActiveConversationId(null);
                      setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date() }]);
                    } catch {
                      // Silent
                    }
                  }}
                />
                <OverflowMenuItem
                  hasDivider
                  itemText={showArchived ? 'Hide archived' : 'Show archived'}
                  onClick={() => setShowArchived((v) => !v)}
                />
              </OverflowMenu>
            )}
          </div>
        )}
      </div>

      <Modal
        open={renameModalOpen}
        modalHeading="Rename conversation"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setRenameModalOpen(false)}
        onRequestSubmit={async () => {
          if (!renamingId) return;
          try {
            const title = renameValue.trim();
            await chatApi.updateConversation(renamingId, { title });
            setConversations((prev) =>
              prev.map((c) => (c.id === renamingId ? { ...c, title } : c)),
            );
          } finally {
            setRenameModalOpen(false);
          }
        }}
      >
        <TextInput
          id="rename-conv-input"
          labelText="Conversation name"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && renamingId) {
              const title = renameValue.trim();
              void chatApi
                .updateConversation(renamingId, { title })
                .then(() => {
                  setConversations((prev) =>
                    prev.map((c) => (c.id === renamingId ? { ...c, title } : c)),
                  );
                  setRenameModalOpen(false);
                })
                .catch(() => setRenameModalOpen(false));
            }
          }}
          autoFocus
        />
      </Modal>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start'
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: message.role === 'assistant' 
                ? 'var(--cds-interactive-01)' 
                : 'var(--cds-layer-accent-01)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {message.role === 'assistant' ? (
                <Bot size={16} style={{ fill: 'white' }} />
              ) : (
                <User size={16} style={{ fill: 'white' }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                backgroundColor: message.role === 'assistant' 
                  ? 'var(--cds-layer-02)' 
                  : 'var(--cds-layer-accent-02)',
                padding: density === 'compact' ? '0.5rem 0.625rem' : '0.75rem',
                borderRadius: '8px',
                fontSize: tokens.body,
                lineHeight: 1.5,
                color: 'var(--cds-text-primary)',
                wordBreak: 'break-word',
              }}>
                {message.role === 'assistant' ? (
                  <MessageBody
                    lookup={message.nodeLookup}
                    onNodeClick={onCitationClick}
                    density={density}
                  >
                    {message.content}
                  </MessageBody>
                ) : (
                  message.content
                )}
              </div>
              {/* Co-Ontologist proposal cards — rendered below the text bubble */}
              {message.proposals && message.proposals.length > 0 && (
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {message.proposals.map((proposal, idx) => {
                    // An add_relation is blocked until every add_node in the same message
                    // that matches its source or target label has been accepted.
                    // This prevents linking to a node that hasn't been created yet.
                    let blockedBy: string | undefined;
                    if (proposal.kind === 'add_relation') {
                      const refLabels = [proposal.sourceNodeLabel, proposal.targetNodeLabel];
                      const blocker = message.proposals!.find((p, i) => {
                        if (p.kind !== 'add_node') return false;
                        if (!refLabels.includes(p.label)) return false;
                        return message.proposalDecisions?.[i] !== 'accepted';
                      });
                      if (blocker) blockedBy = blocker.label;
                    }

                    const decision = message.proposalDecisions?.[idx] ?? null;
                    const nearKey = `${message.id}:${idx}`;
                    const nearMatches = nearMatchesMap[nearKey];

                    const applyAccept = (p: GraphProposal) => {
                      setMessages((prev) =>
                        prev.map((m) =>
                          m.id !== message.id
                            ? m
                            : { ...m, proposalDecisions: { ...m.proposalDecisions, [idx]: 'accepted' as const } },
                        ),
                      );
                      if (p.kind === 'add_node') onAddNode?.(p);
                      else if (p.kind === 'add_relation') onAddEdge?.(p);
                      else if (p.kind === 'edit_node') onUpdateNode?.(p);
                      // Trigger auto-save only for persisted ontologies
                      // (unsaved ones have no id and cannot be saved without a name).
                      if (ontology.id) onProposalAccepted?.();
                    };

                    return (
                      <ProposalCard
                        key={idx}
                        proposal={proposal}
                        decision={decision}
                        disabledReason={blockedBy ? `Accept "${blockedBy}" first` : undefined}
                        nearMatches={nearMatches}
                        onAccept={(p) => {
                          if (p.kind !== 'add_node' || !ontology.id) {
                            applyAccept(p);
                            return;
                          }
                          // For add_node: run a similarity search first.
                          // High threshold (0.92) = near-identical label/definition only.
                          chatApi.retrieveContext({
                            ontologyId: ontology.id,
                            query: p.label + (p.description ? ' ' + p.description : ''),
                            seedLimit: 3,
                            threshold: 0.92,
                          }).then((ctx) => {
                            const matches: NearMatch[] = ctx.seeds.map((s) => ({
                              nodeId: s.nodeId,
                              label: s.label,
                              similarity: s.similarity,
                            }));
                            if (matches.length > 0) {
                              // Show the warning — don't apply yet
                              setNearMatchesMap((prev) => ({ ...prev, [nearKey]: matches }));
                            } else {
                              applyAccept(p);
                            }
                          }).catch(() => {
                            // Similarity check failed (embeddings not ready, etc.) — apply without warning
                            applyAccept(p);
                          });
                        }}
                        onConfirmDespiteDuplicate={(p) => {
                          setNearMatchesMap((prev) => {
                            const next = { ...prev };
                            delete next[nearKey];
                            return next;
                          });
                          applyAccept(p);
                        }}
                        onReject={() => {
                          // Clear any pending near-matches for this card too
                          setNearMatchesMap((prev) => {
                            const next = { ...prev };
                            delete next[nearKey];
                            return next;
                          });
                          setMessages((prev) =>
                            prev.map((m) =>
                              m.id !== message.id
                                ? m
                                : { ...m, proposalDecisions: { ...m.proposalDecisions, [idx]: 'rejected' as const } },
                            ),
                          );
                        }}
                      />
                    );
                  })}
                </div>
              )}
              {message.examples && message.examples.length > 0 && (
                <div style={{ marginTop: '0.625rem' }}>
                  <div style={{
                    fontSize: tokens.meta,
                    color: 'var(--cds-text-secondary)',
                    marginBottom: '0.375rem',
                  }}>
                    Try asking:
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.375rem',
                  }}>
                    {message.examples.map((ex) => (
                      <button
                        key={ex}
                        onClick={() => setInput(ex)}
                        style={{
                          fontSize: tokens.meta,
                          padding: '0.3rem 0.75rem',
                          borderRadius: '1rem',
                          border: '1px solid var(--cds-border-interactive)',
                          background: 'var(--cds-layer-01)',
                          color: 'var(--cds-link-01)',
                          cursor: 'pointer',
                          lineHeight: 1.4,
                          fontFamily: 'inherit',
                          textAlign: 'left',
                        }}
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {message.citations && message.citations.length > 0 && (
                <div style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.25rem',
                  alignItems: 'center',
                }}>
                  <span style={{
                    fontSize: tokens.meta,
                    color: 'var(--cds-text-secondary)',
                    marginRight: '0.25rem',
                  }}>
                    Sources:
                  </span>
                  {message.citations.map((c) => (
                    <Tag
                      key={c.id}
                      type="blue"
                      size="sm"
                      onClick={() => onCitationClick?.(c.nodeId)}
                      style={{ cursor: onCitationClick ? 'pointer' : 'default' }}
                      title={`${c.label} — similarity ${c.similarity.toFixed(2)}${
                        c.definition ? `\n${c.definition}` : ''
                      }`}
                    >
                      {c.label}
                    </Tag>
                  ))}
                  {typeof message.totalNodes === 'number' && (
                    <span style={{
                      fontSize: tokens.meta,
                      color: 'var(--cds-text-helper)',
                      marginLeft: '0.25rem',
                    }}>
                      (of {message.totalNodes} concept{message.totalNodes === 1 ? '' : 's'})
                    </span>
                  )}
                </div>
              )}
              <div style={{
                fontSize: tokens.meta,
                color: 'var(--cds-text-secondary)',
                marginTop: '0.25rem'
              }}>
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
        {isLoading && (
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'flex-start'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--cds-interactive-01)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Bot size={16} style={{ fill: 'white' }} />
            </div>
            <div style={{
              backgroundColor: 'var(--cds-layer-02)',
              padding: density === 'compact' ? '0.5rem 0.625rem' : '0.75rem',
              borderRadius: '8px',
              fontSize: tokens.body,
              color: 'var(--cds-text-secondary)'
            }}>
              Thinking...
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ 
        padding: '1rem', 
        borderTop: '1px solid var(--cds-border-subtle-01)',
        backgroundColor: 'var(--cds-layer-02)'
      }}>
        {error && (
          <InlineNotification
            kind="warning"
            lowContrast
            title="Chat error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            style={{ marginBottom: '0.5rem', maxWidth: '100%' }}
          />
        )}
        {/*
          Show the manual re-index CTA only when:
            (a) we observed a degraded retrieval on a real query
                (`pendingMissing > 0` set inside sendMessage), OR
            (b) background polling has reported missing embeddings for longer
                than REINDEX_CTA_DELAY_MS (`showReindexCta`), which means the
                automatic backfill is stalled and the user needs an escape
                hatch.
          During the normal 5–10s post-save backfill window we render
          nothing here — the header icon already conveys the in-progress
          state without nagging the user.
        */}
        {(pendingMissing > 0 || showReindexCta) && ontology.id && (
          <div style={{ marginBottom: '0.5rem' }}>
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title="Indexing incomplete"
              subtitle={`${
                pendingMissing || indexStatus?.embeddingsMissing || 0
              } concept(s) are not yet embedded — vector search may miss them.`}
              style={{ marginBottom: '0.25rem', maxWidth: '100%' }}
            />
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Renew}
              disabled={reindexing}
              onClick={async () => {
                if (!ontology.id) return;
                setReindexing(true);
                try {
                  const res = await chatApi.backfillEmbeddings(ontology.id);
                  setPendingMissing(0);
                  setShowReindexCta(false);
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: `r-${Date.now()}`,
                      role: 'assistant',
                      content: `🔄 Re-indexed **${res.embedded}** concept(s). Try your question again — vector search now covers the whole ontology.`,
                      timestamp: new Date(),
                    },
                  ]);
                } catch (e: any) {
                  setError(e?.message || 'Re-indexing failed');
                } finally {
                  setReindexing(false);
                }
              }}
            >
              {reindexing ? 'Re-indexing…' : 'Re-index now'}
            </Button>
          </div>
        )}
        <Stack gap={4}>
          <TextInput
            id="chat-input"
            labelText=""
            placeholder="Ask about your ontology..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            disabled={isLoading}
          />
          <Button
            kind="primary"
            size="md"
            renderIcon={Send}
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
          >
            Send
          </Button>
        </Stack>
      </div>
    </div>
  );
};

// Made with Bob
